import { DayOfWeek, INotificationFillLevelSettingsDto, INotificationTypeDto } from 'api/api';
import moment from 'moment';
import { Card } from 'primereact/card';
import React, { PureComponent } from 'react';
import { localized, localizedDynamic } from 'state/i18n';
import { OnChangeEvent } from 'view/components/bi-multiselect/bi-multiselect';
import BiButton from 'view/shared/components/buttons/bi-button/bi-button';
import BiTextDialog from 'view/shared/components/dialogs/bi-text-dialog/bi-text-dialog';
import { getDefaultFillLevelSettings, makeINotificationFillLevelSettingDto } from '../../notification-helper';
import FillLevelMultiselect from './fill-level-multiselect';
import { FillLevelTimePicker } from './fill-level-time-picker';
import './fill-level.scss';

const fillLevelSettingsOrDeafault = (
	fillLevelSettings?: INotificationFillLevelSettingsDto[],
	notificationType?: INotificationTypeDto
): INotificationFillLevelSettingsDto[] => {
	if (!notificationType) return [];
	if (!fillLevelSettings) return getDefaultFillLevelSettings(notificationType);
	let filteredSettings: INotificationFillLevelSettingsDto[] = fillLevelSettings.filter(
		set => set.machineType === notificationType.machineType
	);
	if (filteredSettings.length === 0) return getDefaultFillLevelSettings(notificationType);

	return filteredSettings;
};

type Props = {
	onCancel: () => void;
	onSave: (settings: INotificationFillLevelSettingsDto[], notificationTypeId: number) => void;
	notificationType: INotificationTypeDto | undefined;
	fillLevelSettings?: INotificationFillLevelSettingsDto[];
};

interface State {
	fillLevelSettings: INotificationFillLevelSettingsDto[];
}

class FillLevelDialog extends PureComponent<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = {
			fillLevelSettings: fillLevelSettingsOrDeafault(this.props.fillLevelSettings, this.props.notificationType),
		};
	}

	componentDidUpdate(preProps: Props) {
		if (
			preProps.fillLevelSettings !== this.props.fillLevelSettings ||
			preProps.notificationType !== this.props.notificationType
		)
			this.setState({
				fillLevelSettings: fillLevelSettingsOrDeafault(
					this.props.fillLevelSettings,
					this.props.notificationType
				),
			});
	}

	handleFillLevelChange = (e: OnChangeEvent, day: DayOfWeek) => {
		const currentFillLevelSettings = this.props.fillLevelSettings?.filter(
			fls => fls.dayOfWeek === day && e.value.includes(fls.thresholdPercentage)
		);
		let fillLevelSettings: INotificationFillLevelSettingsDto[] = e.value.map((val: number) =>
		{
			const currentFillLevelSetting = currentFillLevelSettings?.find(fls => fls.dayOfWeek === day && fls.thresholdPercentage === val);
			return makeINotificationFillLevelSettingDto(val, day, this.props.notificationType!, currentFillLevelSetting?.specificTimeToNotify)
		}

		);
		fillLevelSettings.push(...this.state.fillLevelSettings.filter(set => set.dayOfWeek !== day));
		this.setState({ fillLevelSettings: fillLevelSettings });
	};

	handleTimeToNotifyChange = (e: OnChangeEvent, fillLevelSetting: INotificationFillLevelSettingsDto) => {
		const fillLevelSettings = this.state.fillLevelSettings.filter(fls => fls !== fillLevelSetting);
		const timeString = e.value === null ? '' : moment(e.value).format('HH:mm:00');
		const newFls = { ...fillLevelSetting, specificTimeToNotify: timeString };
		fillLevelSettings.push(newFls);
		this.setState({ fillLevelSettings: fillLevelSettings });
	};

	makeFillLevelCardsByDay = (day: DayOfWeek): JSX.Element[] => {
		let dayFillLevelsettings: INotificationFillLevelSettingsDto[] = this.state.fillLevelSettings
			.filter(set => set.dayOfWeek === day)
			.sort((a, b) => a.thresholdPercentage - b.thresholdPercentage);
		let cards: JSX.Element[] = dayFillLevelsettings.map(fls => {
			const dateTimeValue = fls.specificTimeToNotify
				? new Date(`${new Date().toISOString().split('T')[0]}T${fls.specificTimeToNotify}`)
				: undefined;

			return (
				<Card key={day + fls.thresholdPercentage}>
					<div>&#8805; {fls.thresholdPercentage}%</div>
					<FillLevelTimePicker
						fillLevelSetting={fls}
						onChange={this.handleTimeToNotifyChange}
						value={dateTimeValue}
					/>
				</Card>
			);
		});

		cards.unshift(
			<FillLevelMultiselect
				key={`${day}filllevelselector`}
				onChange={this.handleFillLevelChange}
				day={day}
				fillLevelSettings={this.state.fillLevelSettings}
			/>
		);
		for (let i: number = cards.length; i < 7; i++) cards.push(<div key={day + i} />);
		return cards;
	};

	makeFillLevelCards = (): JSX.Element => {
		return (
			<div className="fill-level-grid-content">
				{this.makeFillLevelCardsByDay(DayOfWeek.Monday)}
				{this.makeFillLevelCardsByDay(DayOfWeek.Tuesday)}
				{this.makeFillLevelCardsByDay(DayOfWeek.Wednesday)}
				{this.makeFillLevelCardsByDay(DayOfWeek.Thursday)}
				{this.makeFillLevelCardsByDay(DayOfWeek.Friday)}
				{this.makeFillLevelCardsByDay(DayOfWeek.Saturday)}
				{this.makeFillLevelCardsByDay(DayOfWeek.Sunday)}
			</div>
		);
	};

	onSave = () => {
		this.props.onSave(this.state.fillLevelSettings, this.props.notificationType!.id);
	};

	render() {
		if (typeof this.props.notificationType === 'undefined') return null;
		return (
			<BiTextDialog
				id="fill-level-dialog-container"
				onHide={this.props.onCancel}
				visible={true}
				closable={false}
				blockScroll={true}
				title={localized('fillLevel')}
				subtitle={localized('CustomFillLevelExplained')}
			>
				<div className="fill-level-dialog">
					<div className="fill-level-grid-header">
						<b>{localizedDynamic('Weekday.Monday')}</b>
						<b>{localizedDynamic('Weekday.Tuesday')}</b>
						<b>{localizedDynamic('Weekday.Wednesday')}</b>
						<b>{localizedDynamic('Weekday.Thursday')}</b>
						<b>{localizedDynamic('Weekday.Friday')}</b>
						<b>{localizedDynamic('Weekday.Saturday')}</b>
						<b>{localizedDynamic('Weekday.Sunday')}</b>
					</div>

					{this.makeFillLevelCards()}

					<br />
					<div className="flex-end-row margin-top-10px">
						<BiButton
							onClick={this.props.onCancel}
							colorTheme="org-red"
							containerTheme="slim-with-rounded-corners"
							containerClassName="margin-right-24px"
						>
							{localized('Cancel')}
						</BiButton>
						<BiButton
							onClick={this.onSave}
							colorTheme={'org-green'}
							containerTheme="slim-with-rounded-corners"
						>
							{localized('Save')}
						</BiButton>
					</div>
				</div>
			</BiTextDialog>
		);
	}
}
export default FillLevelDialog;
